import { useEffect, useState } from "react";
import * as invoicesActionCreators from "../actions/invoices";
import * as invoicesActionCreatorsGas from '../actions/invoicesGas';
import { useSearchParams } from "react-router";
import { Filter } from "../types/filter";
import { useSettingsContext } from "@/components/Settings/hooks/useSettingsContext";
import { useAppDispatch, useAppSelector } from "@/hooks";
import { default as filterService } from "../services/filter";
import { default as invoicesService } from "../services/invoices";

const actions = {
  electricity: invoicesActionCreators,
  gas: invoicesActionCreatorsGas
}

export interface UseInvoicesProps {
  type: 'electricity' | 'gas';
}

export const useInvoices = ({ 
  type
}: UseInvoicesProps) => {
  const Settings = useSettingsContext();
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const invoices = useAppSelector(state => state.invoices[type].items);
  const currentFilter = useAppSelector(state => state.invoices[type].filters.currentFilter);
  const itemsPage = useAppSelector(state => state.invoices[type].itemsPage);
  const itemsOffset = useAppSelector(state => state.invoices[type].itemsOffset);
  const isFetching = useAppSelector(state => state.invoices[type].isFetching);
  const loaded = useAppSelector(state => state.invoices[type].loaded);
  const errors = useAppSelector(state => state.invoices[type].errors);

  const [fetchRequest, setFetchRequest] = useState({
    filter: currentFilter,
    offset: itemsOffset,
    limit: itemsPage
  });
  const [initialFilter, setInitialFilter] = useState<Filter[]>([]);
  const [initialItemsPage, setInitialItemsPage] = useState(Settings?.invoices?.itemsPerPage ?? 100);

  //Runs when component is mounted/unmounted
  useEffect(() => {
    fetchFilterParams();
    
    return () => {
      // When unmounting set as not loaded for the next time
      dispatch(actions[type].resetInvoices());
    }
  }, []);

  useEffect(() => {  //Runs when the url parameter 'contractName' changes
    const contractName = searchParams.get("contractName");
    if (contractName) {
      setInitialFilter([
        {
          category: { type: "autocomplete", renderText: "Contracte", searchField: "polissa_id.name" },
          value: [contractName], 
          type: 'contains'
        }
      ]);
      setFetchRequest({
        filter: `('polissa_id.name', 'in', ['${contractName}'])`,
        offset: 0, 
        limit: Settings?.invoices?.itemsPerPage ?? 100
      });
    } else {
      setInitialFilter([]);
      setFetchRequest({
        filter: '', 
        offset: 0,
        limit: Settings?.invoices?.itemsPerPage ?? 100
      });
    }
    setInitialItemsPage(Settings?.invoices?.itemsPerPage ?? 100);
  }, [searchParams.get("contractName")]);

  useEffect(() => {
    if (
      fetchRequest.filter !== currentFilter ||
      fetchRequest.offset !== itemsOffset ||
      fetchRequest.limit !== itemsPage
    ) {
      dispatch(actions[type].fetchInvoicesRequest(fetchRequest));
    }
  }, [fetchRequest.filter, fetchRequest.offset, fetchRequest.limit])

  useEffect(() => {
    if (isFetching) {
      fetchData()
    }
  }, [isFetching])

  const fetchFilterParams = () => {
    dispatch(actions[type].fetchInvoicesFilterRequest());
    filterService.fetchFieldParams({
      fields: ["addresses", "contracts", "cups", "vats"],
      type
    })
    .then(res => {
      dispatch(actions[type].receiveInvoicesFilter(res));
    })
    .catch(err => {
      dispatch(actions[type].receiveInvoicesFilter({}));
    });
  }

  const fetchData = (
    filter: string = currentFilter,
    offset: number = itemsOffset,
    limit: number = itemsPage
  ) => {
    invoicesService.fetchInvoices({
      filter, offset, limit, type
    }).then(res => {
      dispatch(actions[type].receiveInvoices(res));
    }).catch(err => {
      dispatch(actions[type].receiveInvoices(
        { items: [], n_items: 0, offset, limit }, err));
    })
  };

  const applySearchFilter = (newFilters: string, newItemsPage: number) => {
    const newItemsOffset = newItemsPage !== itemsPage ? 0 : itemsOffset;
    setFetchRequest({
      filter: newFilters, 
      offset: newItemsOffset,
      limit: newItemsPage
    });
  };

  const handleItemsOffset = (newOffset: number) => {
    setFetchRequest({
      filter: currentFilter, 
      offset: newOffset,
      limit: itemsPage
    });
  }; 

  return { 
    invoices,
    loaded,
    errors,
    initialFilter,
    initialItemsPage,
    fetchData,
    applySearchFilter,
    handleItemsOffset
  }
}

